/**
 * CPV-MT-01 F1 · ScopeContext — which plants the CPV screens are about (replaces PlantContext for CPV).
 *
 * It reads, once per sign-in:
 *   GET /bp/org/tree/?surface=cpv     site → plant (→ unit), only the plants the person may read on CPV; each plant says
 *                                     whether it is the home plant (`home`) and whether the person may act on it (`act`)
 *   GET /bp/org/scope-preference/     the saved header choice {site, plant: [] | 'all', unit}
 * and saves a new choice with PUT /bp/org/scope-preference/. The choice is sent on every CPV read (scopeStore + the
 * interceptor in utils/api.js) as `plant=all` or `plant=1,2`; a person who may read one plant sends nothing (the server's
 * default is the home plant).
 *
 * What the client works out is display only — the server intersects every request with the person's reach:
 *   inView    the plant ids the screens show (the choice ∩ readable; nothing chosen → the home plant)
 *   multi     more than one plant in view: lists show a Plant column (a column, never a filter)
 *   actInView the plants in view the person may act on (a write acts on the one plant in view; with several, the person
 *             narrows the header choice — openChooser() opens it; no page has a plant selector of its own, 21 Oct 2026)
 */
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Axios from 'axios';
import { useSelector } from 'react-redux';
import { setScopeParams } from './scopeStore';

export const ALL_PLANTS = 'all';
const TREE_URL = '/bp/org/tree/';
const TREE_SURFACE = { surface: 'cpv' };
const PREFERENCE_URL = '/bp/org/scope-preference/';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token') || ''}` } });
const unwrap = (r) => r?.data?.data ?? r?.data;

/** Every readable plant, each with its site ({id, code, name} or null), in tree order. */
export function plantsOf(tree) {
  if (!tree) return [];
  const out = [];
  (tree.sites || []).forEach((s) => (s.plants || []).forEach((p) => out.push({ ...p, site: { id: s.id, code: s.code, name: s.name } })));
  (tree.unassigned || []).forEach((p) => out.push({ ...p, site: null }));
  return out;
}

/** The plant ids a choice names, within `readable` (a site → its readable plants; 'all' → every readable plant). */
export function plantIdsOf(choice, readable) {
  const ids = new Set(readable.map((p) => p.id));
  if (!choice) return [];
  if (choice.plant === ALL_PLANTS) return [...ids];
  const out = new Set((Array.isArray(choice.plant) ? choice.plant : []).filter((id) => ids.has(id)));
  (choice.site || []).forEach((sid) => readable.filter((p) => p.site?.id === sid).forEach((p) => out.add(p.id)));
  return readable.filter((p) => out.has(p.id)).map((p) => p.id);
}

/** The plants in view: the choice within what is readable; nothing chosen (or nothing left) → the home plant. */
function inViewOf(readable, choice, home) {
  const chosen = plantIdsOf(choice, readable);
  if (chosen.length) return chosen;
  if (home != null && readable.some((p) => p.id === home)) return [home];
  return readable.map((p) => p.id);
}

/** The request params: nothing for a one-plant person; 'all' when every readable plant is in view; else the ids. */
function paramsOf(readable, choice, home) {
  if (readable.length <= 1) return {};
  const ids = inViewOf(readable, choice, home);
  return { plant: ids.length === readable.length ? ALL_PLANTS : ids.join(',') };
}

const NO_SCOPE = {
  ready: false, readable: [], byId: {}, home: null, choice: null, inView: [], multi: false, canChoose: false, actInView: [],
  version: 0, tree: null, choose: async () => {}, plantOf: () => null, settled: true, openRequest: 0, openChooser: () => {},
};

const ScopeContext = createContext(NO_SCOPE);

export function ScopeProvider({ children, always = false }) {
  const isLoggedIn = useSelector((state) => state?.auth?.userModel?.isLogin);
  const [tree, setTree] = useState(null);
  const [choice, setChoice] = useState(null);
  const [version, setVersion] = useState(0);
  const [openRequest, setOpenRequest] = useState(0);   // a page asked the header selector to open (openChooser)
  // the first read has answered (or failed): until then the pages wait, so their first read already carries the choice
  const [settled, setSettled] = useState(!(always || isLoggedIn));

  useEffect(() => {
    if (!isLoggedIn && !always) { setTree(null); setChoice(null); setScopeParams({}); setSettled(true); return undefined; }
    let live = true;
    setSettled(false);
    Promise.all([Axios.get(TREE_URL, { params: TREE_SURFACE, ...auth() }).then(unwrap),
      Axios.get(PREFERENCE_URL, auth()).then(unwrap).catch(() => null)])
      .then(([t, pref]) => {
        if (!live || !t || !Array.isArray(t.sites)) return;
        const readable = plantsOf(t);
        const next = pref?.saved ? { site: pref.site || [], plant: pref.plant } : null;
        setScopeParams(paramsOf(readable, next, t.home_plant));   // before the pages read
        setTree(t);
        setChoice(next);
      })
      .catch(() => { /* no tree: the screens keep the server's default (the home plant) */ })
      .finally(() => { if (live) setSettled(true); });
    return () => { live = false; };
  }, [isLoggedIn, always]);

  const readable = useMemo(() => plantsOf(tree), [tree]);
  const byId = useMemo(() => Object.fromEntries(readable.map((p) => [p.id, p])), [readable]);
  const home = tree?.home_plant ?? null;

  const inView = useMemo(() => inViewOf(readable, choice, home), [choice, readable, home]);
  const everyPlant = readable.length > 1 && inView.length === readable.length;
  useEffect(() => { if (tree) setScopeParams(paramsOf(readable, choice, home)); }, [tree, readable, choice, home]);

  const actInView = useMemo(() => {
    const acts = inView.filter((id) => byId[id]?.act);
    return [...acts.filter((id) => id === home), ...acts.filter((id) => id !== home)];
  }, [inView, byId, home]);

  /** Save a new choice ({plant: [ids] | 'all'}) and refresh the screens. Refused → the previous choice stays. */
  const choose = useCallback(async (next) => {
    const body = { site: [], plant: next.plant, unit: [] };
    const saved = unwrap(await Axios.put(PREFERENCE_URL, body, auth()));
    setChoice({ site: saved?.site || [], plant: saved?.plant ?? next.plant });
    setVersion((v) => v + 1);
  }, []);

  /** Open the header location selector (a write that needs one plant while several are in view). */
  const openChooser = useCallback(() => setOpenRequest((n) => n + 1), []);

  /** A row's plant as {id, code, name}: the row's own object, or the id looked up in the tree. */
  const plantOf = useCallback((value) => {
    if (value == null) return null;
    if (typeof value === 'object') return value;
    const p = byId[value];
    return p ? { id: p.id, code: p.code, name: p.name } : { id: value, code: String(value), name: String(value) };
  }, [byId]);

  const value = useMemo(() => ({
    settled, ready: Boolean(tree), tree, readable, byId, home, choice, inView, everyPlant,
    multi: inView.length > 1, canChoose: readable.length > 1, actInView, version, choose, plantOf,
    openRequest, openChooser,
  }), [settled, tree, readable, byId, home, choice, inView, everyPlant, actInView, version, choose, plantOf, openRequest, openChooser]);

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

ScopeProvider.propTypes = { children: PropTypes.node, always: PropTypes.bool };

export const useScope = () => useContext(ScopeContext);

export { ScopeContext };
